import { ReasonCode } from "types/landlord/reports";
import { isAmountNotVerified } from "./fraudRisk";

export type DocumentProblem = {
  label: string;
  description?: string;
};

export const NAME_MISMATCH_LABEL = "Name mismatch";
export const REJECTED_LABEL = "Rejected";

// Most specific first.
const REJECTION_REASON_LABELS: [string, string][] = [
  ["type_mismatch", "Wrong document"],
  ["wrong_document", "Wrong document"],
  ["not_w2", "Wrong document"],
  ["wrong_year", "Wrong year"],
  ["no_income_extracted", "No income found"],
  ["no_wages_extracted", "No income found"],
  ["no_amounts_extracted", "No income found"],
  ["amount_not_verified", "No income found"],
  ["missing_period", "Missing period"],
  ["name_not_present", "Name missing"],
  ["low_quality_score", "Poor file quality"],
  ["is_fraudulent", "Fraud detected"],
  ["high_risk_level", "High risk"],
  ["low_trust_score", "Low trust score"]
];

const toRejectedLabel = (reason?: string) =>
  reason ? `${REJECTED_LABEL} · ${reason}` : REJECTED_LABEL;

const firstRejectionLabel = (reasons: string[]) =>
  REJECTION_REASON_LABELS.find(([reason]) => reasons.includes(reason))?.[1];

const DOCUMENT_PROBLEMS = {
  Unprocessable: { label: REJECTED_LABEL },
  TypeMismatch: { label: toRejectedLabel("Wrong document") },
  NoAmount: { label: "No income amount found" },
  NoTransactions: { label: "No transactions selected" },
  NameMismatch: { label: NAME_MISMATCH_LABEL }
} satisfies Record<string, DocumentProblem>;

const UNPROCESSABLE_DETECT_STATUSES = ["unable_to_process", "unsupported"];

type DocumentProblemSource = {
  verifyType?: string;
  hasTransactions?: boolean;
  detect_status?: string;
  doc_info?: {
    status?: string;
    rejection_reason?: string;
    rejection_reason_description?: string;
  } | null;
  additional_data?: { type_mismatch?: boolean | null } | null;
  name_mismatch?: unknown;
  rejection_reason?: string[] | null;
  source_status?: string;
  sourceStatus?: string;
  status?: string;
};

const toStatus = (value?: string) => value?.toLowerCase() ?? "";

export const resolveDocumentProblems = (row: unknown): DocumentProblem[] => {
  const source = (row ?? {}) as DocumentProblemSource;
  const problems: DocumentProblem[] = [];

  const isUnprocessable =
    UNPROCESSABLE_DETECT_STATUSES.includes(toStatus(source.detect_status)) ||
    toStatus(source.doc_info?.status) === "rejected";

  if (isUnprocessable) {
    const reason =
      source.doc_info?.rejection_reason_description ??
      source.doc_info?.rejection_reason;

    problems.push({
      ...DOCUMENT_PROBLEMS.Unprocessable,
      ...(reason
        ? { description: `Uploaded document rejected: ${reason}` }
        : {})
    });
  }

  // Reasons are also set on documents that verified with warnings.
  const isFailedSource = [
    source.source_status,
    source.sourceStatus,
    source.status
  ].includes("failed");
  const isRejected =
    source.additional_data?.type_mismatch === true ||
    (isFailedSource && Boolean(source.rejection_reason?.length));

  if (source.additional_data?.type_mismatch === true)
    problems.push(DOCUMENT_PROBLEMS.TypeMismatch);
  else if (isRejected)
    problems.push({
      label: toRejectedLabel(firstRejectionLabel(source.rejection_reason ?? []))
    });

  if (!isRejected && isAmountNotVerified(row))
    problems.push(DOCUMENT_PROBLEMS.NoAmount);

  if (source.hasTransactions === false)
    problems.push(DOCUMENT_PROBLEMS.NoTransactions);

  if (source.name_mismatch) problems.push(DOCUMENT_PROBLEMS.NameMismatch);

  return problems;
};

export const toFraudRiskLines = (
  reasonCodes?: ReasonCode[],
  summary?: string | null
): string[] => [
  ...(reasonCodes ?? [])
    .filter((item) => Boolean(item?.description))
    .map((item) =>
      item.confidence
        ? `${item.description}: ${item.confidence} confidence`
        : String(item.description)
    ),
  ...(summary ? [summary] : [])
];
